import { useResponse } from "contexts/response";
import { useCallback } from "react";
import { Contract } from "types";

const BASE_URL = new URL('/api/contracts', window.location.origin);

export const useContractAPI = () => {
  const { handleError, setSuccess } = useResponse();

  const resolveErrorStatus = useCallback(
    (response: Response, json: any) => {
      switch (response.status) {
        case 500:
          handleError('INTERNAL SERVER ERROR');
          return null;
        case 403:
          handleError('FORBIDDEN');
          return null;
        case 401:
          handleError('UNAUTHORIZED');
          return null;
        default:
          handleError(json.message?.match(/"[^"]+"/)?.[0] ?? response.statusText);
          return null;
      }
    }, [handleError]
  );

  const createContract = useCallback(
    async (contract: Contract): Promise<Contract | null> => {
      const options: RequestInit = {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }),
        body: JSON.stringify(contract)
      };

      try {
        const response = await fetch(BASE_URL, options);
        const json = await response.json();

        await Promise.allSettled([response, json]);

        if (response.ok && json.id) {
          setSuccess('Contract Created!');
          return json;
        }

        return resolveErrorStatus(response, json);
      } catch (e) {
        handleError(e);
        return null;
      }
    },
    [handleError, resolveErrorStatus, setSuccess]
  );

  const updateContract = useCallback(
    async (contract: Contract, id: number): Promise<Contract | null> => {
      const options: RequestInit = {
        method: 'PUT',
        headers: new Headers({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }),
        body: JSON.stringify(contract)
      };

      try {
        const response = await fetch(`${BASE_URL}/${id}`, options);
        const json = await response.json();

        await Promise.allSettled([response, json]);

        if (response.ok && json.id) {
          setSuccess('Contract Updated!');
          return json;
        }

        return resolveErrorStatus(response, json);
      } catch (e) {
        handleError(e);
        return null;
      }
    },
    [handleError, resolveErrorStatus, setSuccess]
  );

  return { createContract, updateContract };
}